import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import { useAppContext } from '../contexts/Storage';
import { TransactionItem } from '../models/TransactionItem';
import { TransactionEntity } from '../models/TransactionEntity';
import { TransactionFetchFilter } from '../models/TransactionFetchFilter';
import { EnhancedTransactionForm } from '../components/Transaction';
import NewTransactionEntitySidebar from '../components/TransactionEntity/NewTransactionEntitySidebar';
import {
  useCreateTransaction,
  useUpdateTransaction,
  useGetTransactions,
  CreateTransactionData,
  UpdateTransactionData,
} from '../api/transactions';
import bot from '../assets/images/bot.png';
import info from '../assets/images/information.png';

const NewTransactionPage: React.FC = () => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation() as { state?: { transaction?: TransactionItem } };
  const { recordId: recordIdParam, transactionId: transactionIdParam } = useParams<{
    recordId?: string;
    transactionId?: string;
  }>();
  const recordIdFromUrl = recordIdParam ? Number(recordIdParam) : undefined;
  const transactionId = transactionIdParam ? Number(transactionIdParam) : undefined;
  const { setNavLinks } = useNavLinksContext();
  const { selectedRecord } = useAppContext();
  const [isEditMode, setIsEditMode] = useState(false);
  const [transactionToEdit, setTransactionToEdit] = useState<TransactionItem | null>(null);

  // Use global selected record for new transactions, URL param for edit mode
  const selectedRecordId = isEditMode && recordIdFromUrl ? recordIdFromUrl : selectedRecord?.id;
  const recordName = isEditMode && recordIdFromUrl ? undefined : selectedRecord?.record_name;

  // Create and update mutations
  const createTransactionMutation = useCreateTransaction();
  const updateTransactionMutation = useUpdateTransaction();

  // Fetch single transaction when in edit mode without location state (e.g. refresh)
  const editFilters = useMemo(() => {
    const f = new TransactionFetchFilter();
    if (recordIdFromUrl !== undefined) f.record_id = recordIdFromUrl;
    if (transactionId !== undefined) f.id = transactionId;
    f.limit = 1;
    f.page = 1;
    return f;
  }, [recordIdFromUrl, transactionId]);

  const { data: editTransactionData } = useGetTransactions({
    filters: editFilters,
    enabled: isEditMode && !!recordIdFromUrl && !!transactionId && !locationState?.transaction,
  });

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<'action' | 'direct_object' | 'indirect_object' | 'paymentMedia'>('action');
  const [formData, setFormData] = useState<Partial<TransactionItem>>({});

  // Track if form is searching (for analyzing indicator)
  const [isFormSearching, setIsFormSearching] = useState(false);

  // Set navigation links for the New Transaction page
  useEffect(() => {
    const links = [
      { to: '/dashboard', label: 'Dashboard' },
      { to: '/records', label: 'Records' },
      ...(selectedRecord ? [{ to: '/records/transactions', label: selectedRecord.record_name || 'Transactions' }] : []),
    ];

    setNavLinks({ links });
  }, [setNavLinks, selectedRecord]);

  // Edit mode: set when we have transactionId in URL
  useEffect(() => {
    setIsEditMode(!!transactionId);
  }, [transactionId]);

  // Load transaction to edit: from location state (navigate from list) or from API (refresh)
  useEffect(() => {
    if (!transactionId) {
      setTransactionToEdit(null);
      return;
    }
    if (locationState?.transaction && locationState.transaction.id === transactionId) {
      setTransactionToEdit(locationState.transaction);
      return;
    }
    if (editTransactionData?.transactions?.length) {
      setTransactionToEdit(editTransactionData.transactions[0]);
    }
  }, [transactionId, locationState?.transaction, editTransactionData?.transactions]);

  const buildTransactionPayload = (
    transactionData: Partial<TransactionItem>
  ): CreateTransactionData | UpdateTransactionData | null => {
    if (
      !transactionData.dateTime ||
      !transactionData.action?.id ||
      !transactionData.direct_object?.id ||
      !transactionData.paymentMedia?.id ||
      transactionData.amount === undefined ||
      !selectedRecordId
    ) {
      return null;
    }
    const formattedDate = transactionData.dateTime.includes('T')
      ? transactionData.dateTime.replace('T', ' ').slice(0, 19)
      : transactionData.dateTime;
    const dateStr = formattedDate.length === 19 ? formattedDate : formattedDate + ':00';

    return {
      transaction_record_id: selectedRecordId,
      transaction_date: dateStr,
      transaction_action_id: transactionData.action.id,
      transaction_object_id: transactionData.direct_object.id,
      transaction_indirect_object_id: transactionData.indirect_object?.id ?? null,
      transaction_media_id: transactionData.paymentMedia.id,
      transaction_quantity: transactionData.qty ?? 1,
      transaction_total_amount: transactionData.amount,
    };
  };

  const handleCreateTransaction = async (transactionData: Partial<TransactionItem>) => {
    if (!selectedRecordId) {
      console.error('No record selected');
      return;
    }

    const apiData = buildTransactionPayload(transactionData);
    if (!apiData) {
      console.error('Missing required fields');
      return;
    }

    if (isEditMode && transactionId) {
      updateTransactionMutation.mutate(
        { transactionId, data: apiData },
        {
          onSuccess: () => {
            navigate('/records/transactions');
          },
        }
      );
      return;
    }

    createTransactionMutation.mutate(
      { data: apiData },
      {
        onSuccess: () => {
          navigate('/records/transactions');
        },
      }
    );
  };

  const handleCancel = () => {
    navigate('/records/transactions');
  };

  const handleAddEntity = (field: 'action' | 'direct_object' | 'indirect_object' | 'paymentMedia') => {
    setSelectedField(field);
    setSidebarOpen(true);
  };

  const handleSelectEntity = (entity: TransactionEntity) => {
    setFormData(prev => ({ ...prev, [selectedField]: entity }));
    setSidebarOpen(false);
  };

  const handleFormDataChange = useCallback((newFormData: Partial<TransactionItem>) => {
    setFormData({ ...newFormData });
  }, []);

  // Show message if no record selected and not in edit mode
  if (!isEditMode && !selectedRecord) {
    return (
      <div className="h-shink-from-navbar h-fill-from-navbar pl-2 bg-bg_grey_light text-dark overflow-y-scroll">
        <div className="bg-light rounded-lg p-8 text-center">
          <p className="text-grey_dark mb-4">Please select a record to create a transaction.</p>
          <Link
            to="/records"
            className="text-primary hover:text-primary_dark underline"
          >
            Go to Records
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar pl-2 bg-bg_grey_light text-dark overflow-y-scroll">
      <div className="">

        {/* Two-column layout for desktop, stacked for mobile */}
        <div className="flex flex-col lg:flex-row gap-2 ">
          {/* Left column: Transaction form - full width on mobile, 3/5 on desktop when sidebar is open */}
          <div
            className={`bg-light rounded-t-lg p-6 transition-all duration-300 ease-out ${sidebarOpen
              ? 'lg:w-3/5'
              : 'w-full'
              }`}
          >

            {/* Page Header */}
            <div className=" flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
              <div className="w-full flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-xl">{isEditMode ? 'Edit Transaction' : 'New Transaction'}</h3>
                  {recordName && (
                    <p className="text-sm text-grey_dark mt-1">Record: {recordName}</p>
                  )}
                </div>

                {/* Analyzing Container - shows when form is searching */}
                {isFormSearching && (
                  <div className="p-2 md:px-6 border border-grey_light rounded-lg text-center flex justify-between items-center">
                    <div className="text-sm text-secondary animate-pulse">
                      Searching...
                    </div>
                    <img
                      src={bot}
                      alt='bot'
                      className='w-8 h-8 rounded-circle ml-2'
                    />
                  </div>
                )}

              </div>
            </div>

            <hr className="flex-grow border-t border-grey_light mb-4" />

            <EnhancedTransactionForm
              formData={formData}
              onFormDataChange={handleFormDataChange}
              onSubmit={handleCreateTransaction}
              onCancel={handleCancel}
              transaction={transactionToEdit || undefined}
              isEditMode={isEditMode}
              isSubmitting={createTransactionMutation.isPending || updateTransactionMutation.isPending}
              onAddEntity={handleAddEntity}
              onSearchStateChange={setIsFormSearching}
            />

            <hr className="flex-grow border-t border-grey_light my-8" />


            {/* Help Section */}
            <div className="mt-8 bg-light rounded-lg border-grey-light border p-3 text-grey_dark">
              <h4 className="flex items-center text-lg mb-3">Transaction Guide
                <img
                  src={info}
                  alt='info'
                  className='w-4 h-4 mx-2'
                />
              </h4>
              <hr className="flex-grow border-t border-grey_light mb-4" />
              <div className="space-y-3 text-sm text-grey_dark">
                <p className="font-medium mb-2">
                  ACTION is performed on/to/for OBJECT with/from INDIRECT OBJECT through PAYMENT MEDIUM
                </p>
                <div>
                  <h5 className="font-medium mb-1">TYPES:</h5>
                  <div className="ml-2">
                    <div className="mb-2">
                      <h6 className="font-medium">i. ACTION</h6>
                      <div className="ml-4">
                        <p>a. Credit or CR: Received or Income.</p>
                        <p>b. Debit or DR: Outgoing or Expenditure</p>
                      </div>
                    </div>
                    <div className="mb-2">
                      <h6 className="font-medium">ii. OBJECT and INDIRECT OBJECT</h6>
                      <div className="ml-4">
                        <p>a. Tradable: Can be resold, for example, goods.</p>
                        <p>b. Payable: Should be repaid, for example, loans and Credit cards.</p>
                        <p>c. Third party: e.g., a vendor, a business, or any other entity</p>
                      </div>
                    </div>
                    <div>
                      <h6 className="font-medium">iii. PAYMENT MEDIA</h6>
                      <div className="ml-4">
                        <p>means of payment, e.g., banks, cash, crypto, e.t.c.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right column: Entity sidebar or empty state */}
          <div className={`transition-all duration-300 ease-out ${sidebarOpen ? 'lg:w-2/5' : 'lg:w-0 hidden lg:block'
            }`}>
            {sidebarOpen ? (
              <NewTransactionEntitySidebar
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                onSelectEntity={handleSelectEntity}
              />
            ) : (
              <>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewTransactionPage;
